import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Building, Ticket, CheckCircle2, Archive, FolderKanban, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

const COLORS = ['hsl(162, 100%, 45%)', 'hsl(217, 91%, 60%)', 'hsl(258, 90%, 66%)', 'hsl(25, 95%, 53%)', 'hsl(340, 82%, 52%)'];

function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [deskStats, setDeskStats] = useState(null);
  const [projectsStats, setProjectsStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, deskRes, projectsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/zoho-desk/stats`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/zoho/projects-stats`, { credentials: 'include' })
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (deskRes.ok) {
        const deskData = await deskRes.json();
        setDeskStats(deskData);
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjectsStats(projectsData);
      }
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Admin Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  // Use Zoho Desk stats if available
  const statusChartData = (deskStats?.connected ? deskStats?.tickets_by_status : stats?.tickets_by_status)?.map(item => ({
    name: item._id,
    value: item.count
  })) || [];

  const priorityChartData = (deskStats?.connected ? deskStats?.tickets_by_priority : stats?.tickets_by_priority)?.map(item => ({
    name: item._id,
    count: item.count
  })) || [];

  // Ticket counts from Zoho Desk
  const openTickets = deskStats?.connected ? deskStats.open_tickets : (stats?.open_tickets || 0);
  const closedTickets = deskStats?.connected ? deskStats.closed_tickets : (stats?.closed_tickets || 0);
  const archivedTickets = deskStats?.connected ? (deskStats.archived_tickets || 0) : 0;
  const totalTickets = deskStats?.connected ? deskStats.total_tickets : (stats?.total_tickets || 0);

  // Projects stats
  const totalProjects = projectsStats?.total_projects || 0;
  const assignedProjects = projectsStats?.assigned_projects || 0;

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="space-y-6">
        {/* Top Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-secondary">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-8 w-8 text-secondary" />
            </div>
            <p className="text-3xl font-mono font-bold">{stats?.total_users || 0}</p>
            <p className="text-sm text-muted-foreground font-mono">Total Users</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-primary">
            <div className="flex items-center justify-between mb-2">
              <Building className="h-8 w-8 text-primary" />
            </div>
            <p className="text-3xl font-mono font-bold">{stats?.total_companies || 0}</p>
            <p className="text-sm text-muted-foreground font-mono">Companies</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-accent">
            <div className="flex items-center justify-between mb-2">
              <FolderKanban className="h-8 w-8 text-accent" />
            </div>
            <p className="text-3xl font-mono font-bold">{totalProjects}</p>
            <p className="text-sm text-muted-foreground font-mono">Total Projects</p>
            {projectsStats?.connected && (
              <p className="text-xs text-primary font-mono mt-1">{assignedProjects} assigned</p>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-secondary">
            <div className="flex items-center justify-between mb-2">
              <ClipboardList className="h-8 w-8 text-secondary" />
            </div>
            <p className="text-3xl font-mono font-bold">{totalTickets}</p>
            <p className="text-sm text-muted-foreground font-mono">Total Tickets</p>
            {deskStats?.connected && <p className="text-xs text-primary font-mono mt-1">via Zoho Desk</p>}
          </div>
        </div>

        {/* Ticket Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-lg p-6 border-l-4 border-l-accent">
            <div className="flex items-center justify-between mb-2">
              <Ticket className="h-8 w-8 text-accent" />
              <div className="h-2 w-2 rounded-full bg-accent animate-pulse"></div>
            </div>
            <p className="text-3xl font-mono font-bold">{openTickets}</p>
            <p className="text-sm text-muted-foreground font-mono">Open Tickets</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-l-4 border-l-primary">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <p className="text-3xl font-mono font-bold">{closedTickets}</p>
            <p className="text-sm text-muted-foreground font-mono">Closed Tickets</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-l-4 border-l-muted-foreground">
            <div className="flex items-center justify-between mb-2">
              <Archive className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-3xl font-mono font-bold">{archivedTickets}</p>
            <p className="text-sm text-muted-foreground font-mono">Archived Tickets</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-mono font-bold mb-4">Tickets by Status</h3>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(223, 17%, 8%)', border: '1px solid hsl(226, 18%, 18%)' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No ticket data available
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-mono font-bold mb-4">Tickets by Priority</h3>
            {priorityChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={priorityChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(226, 18%, 18%)" />
                  <XAxis dataKey="name" stroke="hsl(0, 0%, 63.9%)" />
                  <YAxis stroke="hsl(0, 0%, 63.9%)" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(223, 17%, 8%)', border: '1px solid hsl(226, 18%, 18%)' }} />
                  <Bar dataKey="count" fill="hsl(162, 100%, 45%)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No priority data available
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboard;
